import { FeedbackModalStep } from './feedbackModal';
import { isProductionWebApp, isStagingWebApp } from './stagingWebApp';

export interface PendingFeedbackIntent {
  initialStep: FeedbackModalStep;
  returnToRoute: string | null;
  sourceTag: string | null;
}

const FEEDBACK_INTENT_STORAGE_KEY = 'trafficlite.pendingFeedbackIntent.v1';
const VALID_STEPS: FeedbackModalStep[] = ['main', 'issue', 'feature'];

const isEligibleFeedbackHost = () => isStagingWebApp() || isProductionWebApp();

const normalizeReturnToRoute = (rawRoute: string | null): string | null => {
  const trimmed = String(rawRoute || '').trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return null;

  let normalized = trimmed;
  if (normalized.startsWith('#')) {
    normalized = normalized.slice(1);
  }
  if (!normalized.startsWith('/')) {
    normalized = `/${normalized}`;
  }
  if (normalized.startsWith('//')) return null;
  return normalized.slice(0, 300);
};

const normalizeInitialStep = (rawStep: string | null): FeedbackModalStep => {
  const nextStep = String(rawStep || '').trim().toLowerCase() as FeedbackModalStep;
  return VALID_STEPS.includes(nextStep) ? nextStep : 'main';
};

export const captureFeedbackIntentFromSearchParams = (params: URLSearchParams): boolean => {
  if (!isEligibleFeedbackHost()) return false;
  const intent = String(params.get('intent') || '').trim().toLowerCase();
  if (intent !== 'feedback') return false;

  const payload: PendingFeedbackIntent = {
    initialStep: normalizeInitialStep(params.get('initialStep')),
    returnToRoute: normalizeReturnToRoute(params.get('returnTo')),
    sourceTag: String(params.get('source') || '').trim().slice(0, 120) || null
  };

  try {
    sessionStorage.setItem(FEEDBACK_INTENT_STORAGE_KEY, JSON.stringify(payload));
  } catch (error) {
    console.warn('Could not persist feedback intent for post-auth routing:', error);
    return false;
  }
  return true;
};

export const consumePendingFeedbackIntent = (): PendingFeedbackIntent | null => {
  if (!isEligibleFeedbackHost()) return null;
  let rawPayload = '';
  try {
    rawPayload = sessionStorage.getItem(FEEDBACK_INTENT_STORAGE_KEY) || '';
    sessionStorage.removeItem(FEEDBACK_INTENT_STORAGE_KEY);
  } catch (error) {
    console.warn('Could not read pending feedback intent:', error);
    return null;
  }
  if (!rawPayload) return null;

  try {
    const parsed = JSON.parse(rawPayload) as Partial<PendingFeedbackIntent>;
    return {
      initialStep: normalizeInitialStep(parsed.initialStep || null),
      returnToRoute: normalizeReturnToRoute(parsed.returnToRoute || null),
      sourceTag: String(parsed.sourceTag || '').trim().slice(0, 120) || null
    };
  } catch (error) {
    console.warn('Pending feedback intent payload was invalid JSON:', error);
    return null;
  }
};
